/**
 * OrderResolutionFlow - the BLU-563 split-order RESOLVER (assignment board).
 *
 * Production-app coordinator surface (apps/frontend/PRODUCTION_DESIGN.md):
 * chrome-free under ProductionLayout, h-12 glove-friendly targets, semantic
 * colour for state (amber = still unassigned, emerald = ready), brand purple
 * only on the primary CTA. Supersedes the retired one-shot SplitLocationFlow
 * (pick lines -> ONE destination); the true resolve is an assignment board:
 *
 *   click line items to select -> "Move N to..." picks a Shopify-mapped
 *   location -> repeat until EVERY shippable line is assigned -> review
 *   (consolidate vs split) -> Apply calls the server-side saga
 *   `resolveOrderFulfillment({ orderId, assignments })` which does all the
 *   Shopify fulfilment-order moves + the ERP partition in one mutation.
 *
 * v1 is WHOLE-LINE: each line goes to exactly one location with its full
 * quantity (the server rejects under/over-assignment). The ONE sanctioned
 * modal is the final AlertDialog confirm. Keyboard model mirrors the retired
 * flow: native Space toggles the focused row, Enter advances to confirm (but
 * skips focused buttons/links), Cmd/Ctrl+A selects all, shift-click ranges.
 * Honest toast contract: success names the outcome; unmoved committed stock or
 * a committed error is a persistent warning, never a fake green.
 */
import { useMemo, useRef, useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  ChevronsUpDown,
  ExternalLink,
  GitBranch,
  ImageOff,
  Loader2,
  MapPin,
  PackageX,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { getUserMessage } from '@/lib/trpc-error-utils';
import { getShopifyAdminOrderUrl } from '@/lib/shopify-urls';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useVariantSelection } from '@/features/inventory/hooks/useVariantSelection';
import { useOrderResolutionDetail } from '../hooks/useProductionIssues';
import { EASING } from '@/lib/animations';

type ResolutionDetail = RouterOutputs['productionOrders']['issues']['getOrderResolutionDetail'];
type ResolutionLine = ResolutionDetail['lines'][number];
type ResolutionLocation = ResolutionDetail['locations'][number];

/** A resolution line adapted for useVariantSelection (`id` = line_item_id). */
type BoardLine = ResolutionLine & { id: string };

/** One review-card row (shared PreviewList shape). */
type ReviewLine = {
  id: string;
  title: string;
  sku: string;
  quantity: number;
  imageUrl: string | null;
  /** True when the line's assigned location is one it already sits at. */
  stays: boolean;
};

/** Selects with more options than this feel cramped; switch to a Combobox
 * with typeahead (repo threshold — DESIGN.md and ClientFilterDropdown). */
const TYPEAHEAD_THRESHOLD = 6;

/** The platform Cmd (Mac) vs Ctrl (everything else) glyph for shortcut hints. */
function getModKey(): string {
  if (typeof navigator === 'undefined') return 'Ctrl';
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent) ? '⌘' : 'Ctrl';
}

/** Small keyboard-hint chip (aria-hidden; the accessible label sits on the control). */
function Kbd({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <kbd
      aria-hidden="true"
      className={cn(
        'inline-flex h-4 min-w-4 items-center justify-center rounded border px-1 text-[10px] font-medium leading-none',
        className,
      )}
    >
      {children}
    </kbd>
  );
}

export function OrderResolutionFlow() {
  const { t } = useTranslation('production');
  const navigate = useNavigate();
  const { prodOrderId } = useParams<{ prodOrderId: string }>();
  const [searchParams] = useSearchParams();
  const { organization } = useOrganization();
  const utils = trpc.useUtils();
  const modKey = useMemo(getModKey, []);

  const detailQuery = useOrderResolutionDetail(prodOrderId);
  const detail = detailQuery.data;

  // Assignment state - the board's single source of truth. lineItemId ->
  // locationId (absent / null = unassigned). Owned here; the picker only
  // writes through assignSelected.
  const [assignments, setAssignments] = useState<Map<string, string | null>>(new Map());
  const [showConfirm, setShowConfirm] = useState(false);
  // Lifted so the Enter shortcut yields while the location picker owns the keyboard.
  const [pickerOpen, setPickerOpen] = useState(false);

  const lines = useMemo<BoardLine[]>(
    () => (detail?.lines ?? []).map((l) => ({ ...l, id: l.line_item_id })),
    [detail],
  );
  const selection = useVariantSelection({ items: lines });
  const { selectedIds, selectedCount, isAllSelected, isSomeSelected, toggleSelect, selectAll, clearSelection } =
    selection;
  const total = lines.length;

  // Assignment targets: Shopify-mapped locations only (the saga moves Shopify
  // fulfilment orders); current locations stay in the list so a line can be
  // explicitly kept where it is.
  const mappedLocations = useMemo<ResolutionLocation[]>(
    () =>
      (detail?.locations ?? [])
        .filter((l) => l.shopify_location_id != null)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [detail],
  );
  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of detail?.locations ?? []) map.set(l.id, l.name);
    return map;
  }, [detail]);

  const assignedLocationOf = (lineId: string): string | null => assignments.get(lineId) ?? null;
  const unassignedCount = lines.filter((l) => !assignedLocationOf(l.id)).length;
  const allAssigned = total > 0 && unassignedCount === 0;
  const resolvable = detail?.resolvable === true;

  // Distinct assigned locations, in the order the location list sorts them.
  const distinctAssigned = useMemo(() => {
    if (!allAssigned) return [] as string[];
    const seen = new Set<string>();
    for (const loc of mappedLocations) {
      if (lines.some((l) => assignedLocationOf(l.id) === loc.id)) seen.add(loc.id);
    }
    return [...seen];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAssigned, lines, mappedLocations, assignments]);
  const isConsolidate = distinctAssigned.length === 1;

  // Every exit (back, error, empty, success) returns to the list the operator
  // came FROM. The split tab stays the default: it is the resolver's home, and
  // the entry points that predate BLU-842's `?from` marker all live there.
  const from = searchParams.get('from');
  const fromTab = from === 'on-hold' || from === 'oos' ? from : 'split-orders';
  const goToIssues = () => navigate(`/production/issues?tab=${fromTab}`);

  // Focus target when every line is assigned (the selection strip unmounts).
  const applyRef = useRef<HTMLButtonElement>(null);

  // Assigning or clearing unmounts the selection strip, which would drop focus
  // to <body>. Restore it to the first still-unassigned row's checkbox, or the
  // Apply button when everything is assigned. rAF so it runs after React
  // commits the unmount.
  const restoreFocusAfterStrip = (nextAssignments: Map<string, string | null>) => {
    requestAnimationFrame(() => {
      const firstUnassigned = lines.find((l) => !nextAssignments.get(l.id));
      if (firstUnassigned) {
        document.getElementById(`resolve-li-${firstUnassigned.id}`)?.focus();
      } else {
        applyRef.current?.focus();
      }
    });
  };

  const assignSelected = (locationId: string) => {
    const next = new Map(assignments);
    for (const id of selectedIds) next.set(id, locationId);
    setAssignments(next);
    clearSelection();
    restoreFocusAfterStrip(next);
  };

  const handleClearSelection = () => {
    clearSelection();
    restoreFocusAfterStrip(assignments);
  };

  const resolveMutation = trpc.productionOrders.issues.resolveOrderFulfillment.useMutation({
    onSuccess: (data) => {
      utils.productionOrders.issues.invalidate();
      utils.productionOrders.getOrderQueue.invalidate();
      utils.productionOrders.getQueueCounts.invalidate();

      const resolvedCount = data.resolved_issue_count ?? 0;
      const hadFollowup =
        (data.unmoved?.length ?? 0) > 0 ||
        !!(data as { committed_error?: string }).committed_error;

      if (hadFollowup) {
        // HONEST: the moves landed but committed stock needs reconciling. Not a
        // clean green success. Name the first unmoved sku + count when known.
        const firstUnmoved = data.unmoved?.[0];
        toast.warning(t('issues.resolveFlow.partialTitle'), {
          description: firstUnmoved
            ? t('issues.resolveFlow.partialDescUnmoved', {
                sku: firstUnmoved.sku,
                count: data.unmoved?.length ?? 0,
              })
            : t('issues.resolveFlow.partialDesc'),
          duration: Infinity,
        });
      } else if (data.outcome === 'consolidated') {
        const locId = data.resulting_location_ids?.[0];
        toast.success(
          t('issues.resolveFlow.successConsolidated', {
            location: (locId && locationNameById.get(locId)) || t('issues.resolveFlow.oneLocation'),
          }),
          resolvedCount > 0
            ? { description: t('issues.resolveFlow.resolvedIssues', { count: resolvedCount }) }
            : undefined,
        );
      } else {
        toast.success(
          t('issues.resolveFlow.successSplit', {
            count: data.resulting_location_ids?.length ?? distinctAssigned.length,
          }),
          resolvedCount > 0
            ? { description: t('issues.resolveFlow.resolvedIssues', { count: resolvedCount }) }
            : undefined,
        );
      }
      goToIssues();
    },
    onError: (err) => {
      setShowConfirm(false);
      const code = (err.data as { code?: string } | null | undefined)?.code;
      if (code === 'BAD_REQUEST') {
        // Validation: stay on the board so the operator fixes the assignment.
        toast.error(getUserMessage(err));
      } else {
        // Partial apply / verify failure: the server left the order safely HELD
        // and its message explains the retry semantics (a retry recomputes the
        // plan, so already-moved groups are no-ops). Stay on the board.
        toast.error(t('issues.resolveFlow.failureTitle'), {
          description: getUserMessage(err),
          duration: Infinity,
        });
      }
    },
  });

  const handleConfirmApply = () => {
    if (!detail || !allAssigned || !resolvable) return;
    const payload = lines.map((l) => ({
      lineItemId: l.line_item_id,
      targetLocationId: assignedLocationOf(l.id) as string,
      quantity: l.quantity,
    }));
    if (payload.some((p) => !p.targetLocationId)) return;
    resolveMutation.mutate({ orderId: detail.order_id, assignments: payload });
  };

  const canApply = allAssigned && resolvable && !resolveMutation.isPending;

  // ---- Keyboard shortcuts -------------------------------------------------
  // Enter opens the Apply confirm once every line is assigned; Cmd/Ctrl+A
  // selects all. Each has a visible on-screen control (the Apply button; the
  // Select-all checkbox). Space is NOT rebound - it toggles the focused
  // checkbox row natively. Ignored while a text input is focused or the
  // picker / confirm dialog owns the keyboard.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (showConfirm || pickerOpen || resolveMutation.isPending) return;
      const el = document.activeElement as HTMLElement | null;
      const tag = el?.tagName;
      const isTextInput = tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable === true;

      // Cmd/Ctrl+A -> select all lines.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'a' || e.key === 'A')) {
        if (!isTextInput && total > 0) {
          e.preventDefault();
          selectAll();
        }
        return;
      }

      // Enter -> open the Apply confirm. Skip when a text input OR an
      // already-activatable control (button/link) is focused, so we never
      // double-fire its native click. Radix checkboxes render as <button> but
      // do NOT activate on Enter natively (Space toggles them), so a focused
      // row still lets Enter advance.
      if (e.key === 'Enter' && !isTextInput) {
        const role = el?.getAttribute('role');
        const isOptionControl = role === 'radio' || role === 'checkbox';
        if (!isOptionControl && (tag === 'BUTTON' || tag === 'A' || role === 'button')) return;
        if (canApply) {
          e.preventDefault();
          setShowConfirm(true);
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showConfirm, pickerOpen, resolveMutation.isPending, total, selectAll, canApply]);

  const shopifyUrl = getShopifyAdminOrderUrl(organization?.shopify_domain, detail?.shopify_order_id);
  const isLoading = detailQuery.isLoading;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 space-y-6">
      {/* Header */}
      <div className="space-y-1.5">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 h-8 gap-1.5 text-muted-foreground"
          onClick={goToIssues}
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t('issues.resolveFlow.backToIssues')}
        </Button>
        <div className="flex items-center gap-2">
          <GitBranch className="h-5 w-5 text-threadheads-purple" aria-hidden="true" />
          <h1 className="text-xl font-semibold">{t('issues.resolveFlow.title')}</h1>
        </div>
        <p className="max-w-prose text-sm text-muted-foreground">
          {t('issues.resolveFlow.explainer')}
        </p>
        {detail && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1">
            {shopifyUrl ? (
              <a
                href={shopifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('issues.splitOrders.viewOnShopifyAria', {
                  order: detail.order_name ?? '',
                })}
                className="inline-flex items-center gap-1.5 rounded font-mono text-xl tabular-nums text-threadheads-purple hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                {detail.order_name}
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </a>
            ) : (
              <span className="font-mono text-xl tabular-nums">{detail.order_name}</span>
            )}
            {detail.customer_display && (
              <span className="text-base text-muted-foreground">{detail.customer_display}</span>
            )}
          </div>
        )}
      </div>

      {detailQuery.isError ? (
        <FlowError onBack={goToIssues} />
      ) : isLoading ? (
        <FlowSkeleton />
      ) : !detail ? (
        <FlowError onBack={goToIssues} />
      ) : total === 0 ? (
        <FlowEmpty onBack={goToIssues} />
      ) : mappedLocations.length === 0 ? (
        <FlowNoLocations onBack={goToIssues} />
      ) : (
        <>
          {/* Hard block: batched / in-production orders cannot be re-partitioned.
              The Apply button stays disabled; this banner says why. Destructive
              red — blocked must read as blocked, distinct from the amber
              confirm caution. */}
          {!resolvable && (
            <div className="flex gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-base text-red-900">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span>
                {detail.batched
                  ? t('issues.resolveFlow.notResolvableBatched')
                  : t('issues.resolveFlow.notResolvable')}
              </span>
            </div>
          )}

          {/* Select all / count */}
          <div className="flex min-h-12 items-center gap-3 rounded-md bg-muted/50 px-3 py-2">
            <Checkbox
              id="resolve-select-all"
              checked={isAllSelected ? true : isSomeSelected ? 'indeterminate' : false}
              onCheckedChange={() => selectAll()}
              aria-label={t('issues.resolveFlow.selectAllAria')}
              aria-keyshortcuts="Control+A Meta+A"
            />
            <label
              htmlFor="resolve-select-all"
              className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground"
            >
              {isAllSelected
                ? t('issues.resolveFlow.deselectAll')
                : t('issues.resolveFlow.selectAll')}
              <span
                className="inline-flex items-center gap-0.5"
                title={t('issues.resolveFlow.selectAllShortcut')}
              >
                <Kbd className="border-border bg-background text-muted-foreground">{modKey}</Kbd>
                <Kbd className="border-border bg-background text-muted-foreground">A</Kbd>
              </span>
            </label>
            <span className="ml-auto text-sm tabular-nums text-muted-foreground">
              {t('issues.resolveFlow.selectedOfTotal', { selected: selectedCount, total })}
            </span>
          </div>

          {/* Line rows: checkbox + thumbnail + identity + right-aligned assignment chip */}
          <div className="divide-y divide-border overflow-hidden rounded-md border">
            {lines.map((line) => {
              const isSelected = selectedIds.has(line.id);
              const assignedId = assignedLocationOf(line.id);
              const assignedName = assignedId ? locationNameById.get(assignedId) : null;
              const currentNames = line.current
                .map((c) => locationNameById.get(c.location_id))
                .filter((n): n is string => !!n);
              const cbId = `resolve-li-${line.id}`;
              return (
                <label
                  key={line.id}
                  htmlFor={cbId}
                  className={cn(
                    'flex min-h-12 cursor-pointer select-none items-center gap-3 px-3 py-2.5 transition-colors motion-reduce:transition-none',
                    isSelected ? 'bg-threadheads-purple/5' : 'hover:bg-muted/50',
                  )}
                  onClick={(e) => {
                    // Keyboard activation (Space on the focused checkbox) reports
                    // detail === 0 and is handled by onCheckedChange below; only
                    // intercept REAL mouse clicks so shift-range never double-toggles.
                    if (e.detail === 0) return;
                    e.preventDefault();
                    toggleSelect(line.id, e);
                  }}
                >
                  <span
                    className={cn(
                      'inline-flex shrink-0',
                      isSelected && 'motion-safe:animate-check-enter',
                    )}
                  >
                    <Checkbox
                      id={cbId}
                      checked={isSelected}
                      onCheckedChange={() => toggleSelect(line.id)}
                      // Mouse is owned by the row's onClick (shift-range aware); the
                      // checkbox stays keyboard-focusable so Space toggles it natively.
                      className="pointer-events-none data-[state=checked]:border-threadheads-purple data-[state=checked]:bg-threadheads-purple"
                      aria-label={t('issues.resolveFlow.selectItemAria', { name: line.title })}
                    />
                  </span>
                  <LineThumbnail imageUrl={line.image_url} alt={line.title} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{line.title}</p>
                    <p className="truncate font-mono text-xs text-muted-foreground">
                      {line.shopify_sku ?? line.sku}
                      <span className="ml-2 tabular-nums">
                        {t('issues.resolveFlow.qty', { count: line.quantity })}
                      </span>
                    </p>
                  </div>
                  {/* Right-aligned assignment chip + current location(s) */}
                  <div className="flex shrink-0 flex-col items-end gap-0.5">
                    {assignedName ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-300/80">
                        <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                        {assignedName}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-dashed px-2.5 py-1 text-xs font-medium text-muted-foreground">
                        {t('issues.resolveFlow.unassigned')}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {currentNames.length > 0
                        ? t('issues.resolveFlow.currentAt', { locations: currentNames.join(', ') })
                        : t('issues.resolveFlow.currentUnknown')}
                    </span>
                  </div>
                </label>
              );
            })}
          </div>

          {/* Shift-range discoverability (the shortcut accelerates the visible rows). */}
          {total > 1 && (
            <p className="text-xs text-muted-foreground">{t('issues.resolveFlow.shiftRangeHint')}</p>
          )}

          {/* Review: renders once 100% assigned, above the footer. */}
          {allAssigned && (
            <ReviewSection
              lines={lines}
              assignedLocationOf={assignedLocationOf}
              distinctAssigned={distinctAssigned}
              locationNameById={locationNameById}
            />
          )}

          {/* Sticky footer: selection action strip (when >=1 selected) stacked
              above the unassigned counter + Apply. Sticky so the picker and the
              primary action stay reachable on long line lists. */}
          <div className="sticky bottom-0 z-20 -mx-4 border-t bg-background/95 px-4 backdrop-blur">
            {selectedCount > 0 && (
              <div className="flex flex-wrap items-center gap-3 border-b border-dashed py-3">
                <span className="text-sm font-medium tabular-nums">
                  {t('issues.resolveFlow.selectedCount', { count: selectedCount })}
                </span>
                <LocationPicker
                  locations={mappedLocations}
                  selectedCount={selectedCount}
                  onPick={assignSelected}
                  onOpenChange={setPickerOpen}
                />
                <Button
                  variant="ghost"
                  className="h-12 gap-1.5 text-muted-foreground"
                  onClick={handleClearSelection}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                  {t('issues.resolveFlow.clearSelection')}
                </Button>
              </div>
            )}
            <div className="flex items-center justify-between gap-3 py-3">
              {/* Live status: amber until every line has a location, then emerald.
                  Icon + text, never colour alone. */}
              <div
                role="status"
                aria-live="polite"
                className={cn(
                  'flex items-center gap-2 text-base font-medium',
                  allAssigned ? 'text-emerald-700' : 'text-amber-700',
                )}
              >
                {allAssigned ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />
                ) : (
                  <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />
                )}
                <span>
                  {allAssigned
                    ? t('issues.resolveFlow.statusAllAssigned')
                    : t('issues.resolveFlow.statusUnassigned', { count: unassignedCount })}
                </span>
              </div>
              <Button
                ref={applyRef}
                className="h-12 gap-1.5 bg-threadheads-purple px-6 text-white hover:bg-threadheads-purple/90"
                onClick={() => setShowConfirm(true)}
                disabled={!canApply}
                aria-keyshortcuts="Enter"
                title={t('issues.resolveFlow.applyShortcut')}
              >
                <GitBranch className="h-4 w-4" aria-hidden="true" />
                {t('issues.resolveFlow.apply')}
                <Kbd className="border-white/40 text-white/90">{'↵'}</Kbd>
              </Button>
            </div>
          </div>
        </>
      )}

      {/* The ONE sanctioned modal: confirm-from-page. */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('issues.resolveFlow.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {isConsolidate
                ? t('issues.resolveFlow.confirmConsolidate', {
                    count: total,
                    location: locationNameById.get(distinctAssigned[0] ?? '') ?? '',
                  })
                : t('issues.resolveFlow.confirmSplit', {
                    count: total,
                    locations: distinctAssigned.length,
                  })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{t('issues.resolveFlow.confirmWarning')}</span>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12" disabled={resolveMutation.isPending}>
              {t('issues.resolveFlow.confirmCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmApply();
              }}
              disabled={resolveMutation.isPending}
              className="h-12 gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
            >
              {resolveMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <GitBranch className="h-4 w-4" aria-hidden="true" />
              )}
              {t('issues.resolveFlow.confirmApply')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ===========================================================================
// Location picker: shadcn Select for <=6 mapped locations, cmdk Combobox at 7+
// (repo threshold). Both surface the same "Move N to..." trigger and assign on
// pick. onOpenChange lets the page's Enter shortcut yield to the picker.
// ===========================================================================

function LocationPicker({
  locations,
  selectedCount,
  onPick,
  onOpenChange,
}: {
  locations: ResolutionLocation[];
  selectedCount: number;
  onPick: (locationId: string) => void;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('production');
  const [open, setOpen] = useState(false);
  const setOpenBoth = (next: boolean) => {
    setOpen(next);
    onOpenChange(next);
  };
  const triggerLabel = t('issues.resolveFlow.moveTo', { count: selectedCount });

  if (locations.length <= TYPEAHEAD_THRESHOLD) {
    return (
      <Select
        open={open}
        onOpenChange={setOpenBoth}
        onValueChange={(locationId) => onPick(locationId)}
      >
        <SelectTrigger
          className="h-12 w-[240px] gap-2"
          aria-label={t('issues.resolveFlow.pickLocationAria')}
        >
          <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <SelectValue placeholder={triggerLabel} />
        </SelectTrigger>
        <SelectContent>
          {locations.map((loc) => (
            <SelectItem key={loc.id} value={loc.id} className="min-h-12">
              {loc.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpenBoth}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={t('issues.resolveFlow.pickLocationAria')}
          className="h-12 w-[240px] justify-between gap-2 font-normal"
        >
          <span className="flex min-w-0 items-center gap-2">
            <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="truncate">{triggerLabel}</span>
          </span>
          <ChevronsUpDown
            className="h-4 w-4 shrink-0 text-muted-foreground opacity-60"
            aria-hidden="true"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder={t('issues.resolveFlow.searchLocations')} />
          <CommandList>
            <CommandEmpty>{t('issues.resolveFlow.noLocationMatch')}</CommandEmpty>
            <CommandGroup>
              {locations.map((loc) => (
                <CommandItem
                  key={loc.id}
                  value={`${loc.name} ${loc.id}`}
                  className="min-h-12"
                  onSelect={() => {
                    onPick(loc.id);
                    setOpenBoth(false);
                  }}
                >
                  <MapPin className="mr-2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <span className="truncate">{loc.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ===========================================================================
// Review: one card per distinct assigned location. Header names the outcome
// (consolidate vs split); lines already at their assigned location read
// "stays" so the operator can see what actually moves.
// ===========================================================================

function ReviewSection({
  lines,
  assignedLocationOf,
  distinctAssigned,
  locationNameById,
}: {
  lines: BoardLine[];
  assignedLocationOf: (lineId: string) => string | null;
  distinctAssigned: string[];
  locationNameById: Map<string, string>;
}) {
  const { t } = useTranslation('production');
  const isConsolidate = distinctAssigned.length === 1;

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <h2 className="flex items-center gap-2 font-semibold">
          {isConsolidate ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
          ) : (
            <GitBranch className="h-4 w-4 text-threadheads-purple" aria-hidden="true" />
          )}
          {isConsolidate
            ? t('issues.resolveFlow.reviewConsolidate', {
                location: locationNameById.get(distinctAssigned[0] ?? '') ?? '',
              })
            : t('issues.resolveFlow.reviewSplit', { count: distinctAssigned.length })}
        </h2>
        <p className="text-sm text-muted-foreground">{t('issues.resolveFlow.reviewHint')}</p>
      </div>

      <div className={cn('grid gap-4', !isConsolidate && 'md:grid-cols-2')}>
        {distinctAssigned.map((locId) => {
          const locLines: ReviewLine[] = lines
            .filter((l) => assignedLocationOf(l.id) === locId)
            .map((l) => ({
              id: l.id,
              title: l.title,
              sku: l.shopify_sku ?? l.sku,
              quantity: l.quantity,
              imageUrl: l.image_url,
              stays: l.current.some((c) => c.location_id === locId),
            }));
          const totalUnits = locLines.reduce((sum, l) => sum + l.quantity, 0);
          return (
            <div key={locId} className="space-y-3 rounded-lg border p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <MapPin className="h-4 w-4 shrink-0 text-threadheads-purple" aria-hidden="true" />
                  <h3 className="truncate text-sm font-semibold">
                    {locationNameById.get(locId) ?? locId}
                  </h3>
                </div>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {t('issues.resolveFlow.qty', { count: totalUnits })}
                </span>
              </div>
              <PreviewList items={locLines} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===========================================================================
// Shared pieces (carried over from the retired SplitLocationFlow)
// ===========================================================================

/**
 * Review-card line list with the retired flow's transform/opacity motion:
 * ease-out ~200ms, capped stagger, reduced-motion renders at rest instantly.
 */
function PreviewList({ items }: { items: ReviewLine[] }) {
  const { t } = useTranslation('production');
  const reduce = useReducedMotion();
  return (
    <ul className="divide-y divide-border">
      {items.map((item, i) => (
        <motion.li
          key={item.id}
          initial={reduce ? false : { opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={
            reduce
              ? { duration: 0 }
              : { duration: 0.2, ease: EASING.easeOut, delay: Math.min(i, 6) * 0.03 }
          }
          className="flex items-center gap-3 py-2"
        >
          <LineThumbnail imageUrl={item.imageUrl} alt={item.title} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{item.title}</p>
            <p className="truncate font-mono text-xs text-muted-foreground">{item.sku}</p>
          </div>
          {item.stays && (
            <span className="shrink-0 rounded border border-dashed px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {t('issues.resolveFlow.stays')}
            </span>
          )}
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {t('issues.resolveFlow.qty', { count: item.quantity })}
          </span>
        </motion.li>
      ))}
    </ul>
  );
}

/** Thumbnail with the established fallback (image -> placeholder icon). */
function LineThumbnail({ imageUrl, alt }: { imageUrl: string | null; alt: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted">
      {imageUrl && !failed ? (
        <img
          src={imageUrl}
          alt={alt}
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <ImageOff className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
      )}
    </div>
  );
}

function FlowSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-7 w-full" />
      <div className="space-y-2 rounded-lg border p-4">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    </div>
  );
}

function FlowError({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation('production');
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border py-12 text-center">
      <AlertTriangle className="h-10 w-10 text-destructive" aria-hidden="true" />
      <p className="text-lg font-medium">{t('issues.resolveFlow.loadError')}</p>
      <p className="text-sm text-muted-foreground">{t('issues.resolveFlow.loadErrorDesc')}</p>
      <Button variant="outline" className="mt-1 h-12 gap-1.5" onClick={onBack}>
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('issues.resolveFlow.backToIssues')}
      </Button>
    </div>
  );
}

function FlowEmpty({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation('production');
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border py-12 text-center">
      <PackageX className="h-10 w-10 text-muted-foreground/60" aria-hidden="true" />
      <p className="text-lg font-medium">{t('issues.resolveFlow.emptyTitle')}</p>
      <p className="text-sm text-muted-foreground">{t('issues.resolveFlow.emptyDesc')}</p>
      <Button variant="outline" className="mt-1 h-12 gap-1.5" onClick={onBack}>
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('issues.resolveFlow.backToIssues')}
      </Button>
    </div>
  );
}

function FlowNoLocations({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation('production');
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border py-12 text-center">
      <MapPin className="h-10 w-10 text-muted-foreground/60" aria-hidden="true" />
      <p className="text-lg font-medium">{t('issues.resolveFlow.noLocations')}</p>
      <p className="text-sm text-muted-foreground">{t('issues.resolveFlow.noLocationsDesc')}</p>
      <Button variant="outline" className="mt-1 h-12 gap-1.5" onClick={onBack}>
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('issues.resolveFlow.backToIssues')}
      </Button>
    </div>
  );
}
